import {
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpDownIcon, CheckIcon, FilterIcon, SearchIcon } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTeamApi } from '../../services/api/useTeam.api';
import { useUserApi } from '../../services/api/useUser.api';
import { useUserStatusApi } from '../../services/api/useUserStatus.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { Team } from '../../types/Team/Team.type';
import { User } from '../../types/User/User.type.ts';
import UserRow from './UserRow';
import { UserUpdate } from '../../types/User/UserUpdate.ts';
import { useRoleApi } from '../../services/api/useRole.api.ts';

interface UsersTableProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
}

interface StyledMenuItemProps {
  isActive: boolean;
}

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<StyledMenuItemProps>`
  display: flex;
  justify-content: space-between;
  background-color: ${(props) => (props.isActive ? '#F7FAFC' : 'transparent')};

  & > span {
    font-size: 14px;
    font-weight: ${(props) => (props.isActive ? 600 : 'normal')};
    color: #4a5568;
  }
`;

const SORT_OPTIONS = [
  { value: 'email:asc', label: 'Email (A-Z)' },
  { value: 'email:desc', label: 'Email (Z-A)' },
  { value: 'created_at:desc', label: 'Last created' },
  { value: 'updated_at:desc', label: 'Last modified' },
];

function UsersTable({ snackbarData }: UsersTableProps) {
  const queryClient = useQueryClient();
  const { getUsers, deactivateUser, reInviteUser, updateUser } = useUserApi();
  const { getTeams } = useTeamApi();
  const { getRoles } = useRoleApi();
  const { getUserStatuses } = useUserStatusApi();

  const [statusId, setStatusId] = useState<number | undefined>(undefined);
  const [teamId, setTeamId] = useState<number | undefined>(undefined);
  const [roleId, setRoleId] = useState<number | undefined>(undefined);
  const [sort, setSort] = useState('email:asc');
  const [query, setQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { ref: usersRef, inView: usersInView } = useInView();

  const {
    data: infiniteUsers,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['manageUsers', statusId, teamId, roleId, sort, query],
    queryFn: ({ pageParam = 1 }) =>
      getUsers({
        page: pageParam,
        per_page: 20,
        sort,
        query: query || undefined,
        user_status_id: statusId,
        team_id: teamId,
        role_id: roleId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<User[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const users = infiniteUsers?.pages?.flatMap((page) => page.data) ?? [];

  const {
    data: teamsData,
    fetchNextPage: fetchNextTeams,
    hasNextPage: hasNextTeams,
    isFetchingNextPage: isFetchingNextTeams,
  } = useInfiniteQuery({
    queryKey: ['teams'],
    queryFn: ({ pageParam = 1 }) => getTeams({ page: pageParam, per_page: 40, sort: 'name:asc' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Team[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const teams = teamsData?.pages?.flatMap((page) => page.data) ?? [];

  const { data: userStatuses } = useQuery({
    queryKey: ['userStatuses'],
    queryFn: getUserStatuses,
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const teamName = (id: number) => teams.find((team) => team.id === id)?.name ?? String(id);
  const activeFilterCount =
    (statusId !== undefined ? 1 : 0) + (teamId !== undefined ? 1 : 0) + (roleId !== undefined ? 1 : 0);

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ['manageUsers'] });

  const deactivateMutation = useMutation({
    mutationFn: (email: string) => deactivateUser(email),
    onSuccess: () => {
      invalidateUsers();
      snackbarData('User deactivated successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to deactivate user. Please try again.', 'error'),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (email: string) => deactivateUser(email),
    onSuccess: () => {
      invalidateUsers();
      snackbarData('Invitation cancelled successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to cancel invitation. Please try again.', 'error'),
  });

  const resendInviteMutation = useMutation({
    mutationFn: (email: string) => reInviteUser(email),
    onSuccess: () => {
      invalidateUsers();
      snackbarData('Invitation resent successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to resend invitation. Please try again.', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: UserUpdate }) => updateUser(userId, payload),
    onSuccess: () => {
      invalidateUsers();
      snackbarData('User updated successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to update user. Please try again.', 'error'),
  });

  const isMutating =
    deactivateMutation.isPending ||
    cancelInviteMutation.isPending ||
    resendInviteMutation.isPending ||
    updateMutation.isPending;

  useEffect(() => {
    if (usersInView && fetchNextPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [usersInView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => setQuery(value), 500);
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
        <TextField
          onChange={handleSearchChange}
          value={searchText}
          sx={{ width: 360 }}
          aria-label='Search users'
          placeholder='Search...'
          size='small'
          slotProps={{
            input: {
              sx: { borderRadius: '8px', fontSize: '14px' },
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Badge badgeContent={activeFilterCount} color='primary'>
            <Button
              sx={{ color: '#4a5568' }}
              aria-label='Filter users'
              onClick={(e) => setFilterAnchor(e.currentTarget)}
            >
              <FilterIcon size={16} />
              <span style={{ fontSize: '14px', marginLeft: '6px' }}>Filters</span>
            </Button>
          </Badge>
          <Button
            sx={{ color: '#4a5568' }}
            aria-haspopup='true'
            aria-expanded={sortAnchor ? 'true' : undefined}
            onClick={(e) => setSortAnchor(e.currentTarget)}
          >
            <ArrowUpDownIcon size={16} />
            <span style={{ fontSize: '14px', marginLeft: '6px' }}>Sort by</span>
          </Button>
        </Box>
      </Box>
      <Menu
        slotProps={{ paper: { style: { width: '24ch' } } }}
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SORT_OPTIONS.map((option) => (
          <StyledMenuItem
            key={option.value}
            isActive={sort === option.value}
            onClick={() => {
              setSort(option.value);
              setSortAnchor(null);
            }}
          >
            <span>{option.label}</span>
            {sort === option.value && (
              <ListItemIcon>
                <CheckIcon />
              </ListItemIcon>
            )}
          </StyledMenuItem>
        ))}
      </Menu>
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={2.5} sx={{ padding: 2, minWidth: 430 }}>
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: 500,
            }}
          >
            Filter table by
          </Typography>

          <Stack direction='column' spacing={1.2}>
            <Stack
              direction='row'
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant='body2' sx={{ width: 56 }}>
                Status
              </Typography>
              <FormControl size='small' sx={{ flex: 1 }}>
                <Select
                  displayEmpty
                  value={statusId ?? ''}
                  onChange={(e) => setStatusId(e.target.value === null ? undefined : Number(e.target.value))}
                  renderValue={() =>
                    statusId === undefined ? (
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{
                          color: 'text.disabled',
                        }}
                      >
                        Select status
                      </Typography>
                    ) : (
                      userStatuses?.find((status) => status.id === statusId)?.name
                    )
                  }
                >
                  {(userStatuses ?? []).map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size='small' onClick={() => setStatusId(undefined)} disabled={statusId === undefined}>
                Clear
              </Button>
            </Stack>

            <Stack
              direction='row'
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant='body2' sx={{ width: 56 }}>
                Role
              </Typography>
              <FormControl size='small' sx={{ flex: 1 }}>
                <Select
                  displayEmpty
                  value={roleId ?? ''}
                  onChange={(e) => setRoleId(e.target.value === null ? undefined : Number(e.target.value))}
                  renderValue={() =>
                    roleId === undefined ? (
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{
                          color: 'text.disabled',
                        }}
                      >
                        Select role
                      </Typography>
                    ) : (
                      roles?.find((role) => role.id === roleId)?.name
                    )
                  }
                >
                  {(roles ?? []).map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size='small' onClick={() => setRoleId(undefined)} disabled={roleId === undefined}>
                Clear
              </Button>
            </Stack>

            <Stack
              direction='row'
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant='body2' sx={{ width: 56 }}>
                Team
              </Typography>
              <FormControl size='small' sx={{ flex: 1 }}>
                <Select
                  displayEmpty
                  value={teamId ?? ''}
                  onChange={(e) => setTeamId(e.target.value === null ? undefined : Number(e.target.value))}
                  renderValue={() =>
                    teamId === undefined ? (
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{
                          color: 'text.disabled',
                        }}
                      >
                        Select team
                      </Typography>
                    ) : (
                      teamName(teamId)
                    )
                  }
                  MenuProps={{
                    slotProps: {
                      paper: {
                        onScroll: (event: React.UIEvent<HTMLDivElement>) => {
                          const target = event.target as HTMLDivElement;
                          const position = target.scrollTop + target.clientHeight;
                          if (target.scrollHeight - position <= 1 && hasNextTeams && !isFetchingNextTeams) {
                            fetchNextTeams();
                          }
                        },
                      },
                    },
                  }}
                >
                  {teams.map((team: Team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size='small' onClick={() => setTeamId(undefined)} disabled={teamId === undefined}>
                Clear
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none', maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map((user: User) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    teams={teams}
                    isLoading={isMutating}
                    onDeactivate={(email) => deactivateMutation.mutate(email)}
                    onCancelInvite={(email) => cancelInviteMutation.mutate(email)}
                    onResendInvite={(email) => resendInviteMutation.mutate(email)}
                    onUpdate={(userId, payload) => updateMutation.mutate({ userId, payload })}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', padding: 4, color: 'text.secondary' }}>
                    No users found
                  </TableCell>
                </TableRow>
              )}
              <TableRow ref={usersRef}>
                <TableCell colSpan={5} sx={{ border: 'none', padding: 1 }}>
                  {isFetchingNextPage && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 1,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default UsersTable;
